import React, { useEffect } from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import {
  LayoutDashboard, Bell, CalendarDays, MapPin, Tags, Users, ShieldCheck,
  BarChart3, Building2, LogOut, Home
} from 'lucide-react';

export default function AdminLayout() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role === 'turista') navigate('/prijava');
  }, [user, navigate]);

  if (!user || user.role === 'turista') return null;

  const items = [
    { to: '/admin', label: 'Kontrolna ploča', icon: LayoutDashboard, show: true, end: true },
    { to: '/admin/obavijesti', label: 'Obavijesti', icon: Bell, show: hasPermission('manage_notices') },
    { to: '/admin/dogadjaji', label: 'Događaji', icon: CalendarDays, show: hasPermission('manage_events') || hasPermission('view_reports') },
    { to: '/admin/ponuda', label: 'Turistička ponuda', icon: MapPin, show: hasPermission('manage_offers') },
    { to: '/admin/tipovi', label: 'Tipovi i kategorije', icon: Tags, show: hasPermission('manage_types') },
    { to: '/admin/korisnici', label: 'Korisnici', icon: Users, show: hasPermission('manage_users') || hasPermission('approve_subjects') },
    { to: '/admin/uloge', label: 'Uloge i dozvole', icon: ShieldCheck, show: hasPermission('manage_roles') },
    { to: '/admin/izvjestaji', label: 'Izvještaji', icon: BarChart3, show: hasPermission('view_reports') },
    { to: '/admin/moj-objekat', label: 'Moj objekat', icon: Building2, show: hasPermission('manage_own_object') }
  ].filter((i) => i.show);

  return (
    <div className="min-h-screen bg-[#f4f6fb] flex">
      {/* SIDEBAR */}
      <aside className="w-64 bg-[#001752] text-white flex-col hidden md:flex sticky top-0 h-screen" data-testid="admin-sidebar">
        <div className="p-5 border-b border-white/10">
          <Link to="/"><img src="/logo-mp.png" alt="logo" className="h-11 w-auto brightness-0 invert" /></Link>
          <p className="text-[11px] text-white/50 mt-2 uppercase tracking-wider">IS E-Turizam – Administracija</p>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end} data-testid={`admin-nav-${i.to.split('/').pop() || 'dashboard'}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-colors duration-200 ${isActive ? 'bg-white text-[#001752] font-semibold' : 'text-white/75 hover:bg-white/10 hover:text-white'}`}>
              <i.icon className="h-4 w-4 shrink-0" /> {i.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <p className="text-sm font-semibold truncate">{user.name}</p>
          <p className="text-xs text-white/50 truncate mb-3">{user.email}</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate('/')} data-testid="admin-back-home"
              className="flex-1 bg-transparent border-white/30 text-white hover:bg-white hover:text-[#001752] h-8 text-xs rounded-md transition-colors duration-200">
              <Home className="h-3.5 w-3.5 mr-1" /> Portal
            </Button>
            <Button size="sm" variant="outline" onClick={() => { logout(); navigate('/'); }} data-testid="admin-logout"
              className="flex-1 bg-transparent border-white/30 text-white hover:bg-white hover:text-[#001752] h-8 text-xs rounded-md transition-colors duration-200">
              <LogOut className="h-3.5 w-3.5 mr-1" /> Odjava
            </Button>
          </div>
        </div>
      </aside>

      {/* MOBILNI HEADER + SADRŽAJ */}
      <div className="flex-1 min-w-0">
        <div className="md:hidden bg-[#001752] text-white px-4 py-3 flex items-center justify-between">
          <img src="/logo-mp.png" alt="logo" className="h-9 w-auto brightness-0 invert" />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => navigate('/')} className="bg-white/10 hover:bg-white/20 h-8 text-xs">Portal</Button>
            <Button size="sm" onClick={() => { logout(); navigate('/'); }} className="bg-white/10 hover:bg-white/20 h-8 text-xs">Odjava</Button>
          </div>
        </div>
        <div className="md:hidden flex overflow-x-auto gap-1 bg-white border-b border-slate-100 px-2 py-2">
          {items.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium ${isActive ? 'bg-[#001752] text-white' : 'text-slate-600'}`}>
              {i.label}
            </NavLink>
          ))}
        </div>
        <main className="p-5 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
